import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { formatPhone } from '../lib/phone';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, ConfirmDialog, ui, fmtDate,
} from '../components/adminUi';
import Avatar from '../components/Avatar';
import { providerRegion, providerCategories } from '../lib/providerInfo';
import { isProviderRole } from '../lib/roles';
import { userStatusLabel, moderationStatusLabel } from '../lib/userStatus';

const ROLE_LABELS = { admin: 'Admin', provider: 'Usta', client: 'Mijoz' };
const ROLE_TONE = { admin: 'dark', provider: 'amber', client: 'gray' };

export default function UsersPage() {
  const { auth } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Boshqa sahifalardan (masalan Shikoyatlar) `?search=<telefon>` bilan o'tish mumkin.
  const initialSearch = (() => {
    try { return new URLSearchParams(window.location.search).get('search') || ''; } catch { return ''; }
  })();
  const [search, setSearch] = useState(initialSearch);
  const [query, setQuery] = useState(initialSearch);
  const [roleFilter, setRoleFilter] = useState('');

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  // Bloklash / blokdan chiqarish oldidan tasdiq so'raladigan foydalanuvchi.
  const [confirmUser, setConfirmUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (query.trim()) params.set('search', query.trim());
      if (roleFilter) params.set('role', roleFilter);
      const res = await apiFetch(`/api/admin/users?${params.toString()}`, auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
      if (data.pageSize) setPageSize(Number(data.pageSize));
    } catch {
      setItems([]);
      setFetchError('Foydalanuvchilarni yuklashda xatolik yuz berdi. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token, page, query, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search);
  };

  // Modal yopilgandan keyin kelgan eski javob uni qayta ochmasin.
  const profileReq = useRef(0);
  const closeProfile = () => { profileReq.current += 1; setProfile(null); setProfileLoading(false); };

  const openProfile = async (id) => {
    const req = ++profileReq.current;
    setProfile({ id, __loading: true });
    setProfileLoading(true);
    try {
      const res = await apiFetch(`/api/admin/users/${id}`, auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (req === profileReq.current) setProfile(data);
    } catch {
      if (req === profileReq.current) setProfile({ id, __error: true });
    } finally {
      if (req === profileReq.current) setProfileLoading(false);
    }
  };

  const toggleBlock = async (user) => {
    const action = user.isBlocked ? 'unblock' : 'block';
    setConfirmUser(null);
    setActingId(user.id);
    try {
      const res = await apiFetch(`/api/admin/users/${user.id}/${action}`, auth.token, {
        method: 'POST',
      });
      if (!res.ok) throw new Error();
      // Reflect the new state locally, then refresh from server.
      setItems((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isBlocked: !user.isBlocked } : u))
      );
      if (profile && profile.id === user.id) {
        setProfile((p) => ({ ...p, isBlocked: !user.isBlocked }));
      }
      await load();
    } catch {
      setFetchError('Amalni bajarishda xatolik yuz berdi.');
    } finally {
      setActingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / (pageSize || 20)));

  return (
    <AdminLayout>
      <PageHeader title="Foydalanuvchilar" subtitle="Barcha ro'yxatdan o'tgan foydalanuvchilar">
        <form onSubmit={submitSearch} style={{ display: 'flex', gap: '8px' }}>
          <input
            style={ui.searchInput}
            placeholder="Ism yoki telefon bo'yicha qidirish..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            style={{ ...ui.select, width: 'auto', minWidth: '160px' }}
            value={roleFilter}
            onChange={(e) => { setPage(1); setRoleFilter(e.target.value); }}
          >
            <option value="">Barcha rollar</option>
            <option value="client">Mijoz</option>
            <option value="provider">Usta</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" style={{ ...ui.addBtn, height: '48px' }}>Qidirish</button>
        </form>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty icon="users" title="Foydalanuvchi topilmadi" hint="Qidiruv yoki filtrni o'zgartirib ko'ring." />
      ) : (
        <>
          <div style={ui.tableWrap}>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={{ ...ui.th, width: '48px' }}>#</th>
                  <th style={ui.th}>F.I.O</th>
                  <th style={ui.th}>Telefon</th>
                  <th style={ui.th}>Rol</th>
                  <th style={ui.th}>Holat</th>
                  <th style={ui.th}>Ro'yxatdan o'tgan</th>
                  <th style={{ ...ui.th, textAlign: 'right' }}>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u, i) => (
                  <tr key={u.id} style={ui.tr}>
                    <td style={{ ...ui.td, color: 'var(--color-text-muted)' }}>{(page - 1) * pageSize + i + 1}</td>
                    <td style={{ ...ui.td, fontWeight: '600', color: 'var(--color-text)' }}>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td style={ui.td}>{formatPhone(u.phone) || '—'}</td>
                    <td style={ui.td}>
                      <Badge tone={ROLE_TONE[u.role] || 'gray'}>{ROLE_LABELS[u.role] || u.role || '—'}</Badge>
                    </td>
                    <td style={ui.td}>
                      {u.isBlocked
                        ? <Badge tone="red">Bloklangan</Badge>
                        : <Badge tone="green">Faol</Badge>}
                    </td>
                    <td style={ui.td}>{fmtDate(u.createdAt)}</td>
                    <td style={{ ...ui.td, textAlign: 'right' }}>
                      <div style={ui.actions}>
                        <button
                          style={{ ...ui.btnSm, ...ui.btnBlue }}
                          onClick={() => openProfile(u.id)}
                        >
                          Profil
                        </button>
                        {u.role !== 'admin' && (
                          <button
                            style={{
                              ...ui.btnSm,
                              ...ui.btnLinkDanger,
                              ...(actingId === u.id ? ui.btnDisabled : {}),
                            }}
                            onClick={() => setConfirmUser(u)}
                            disabled={actingId === u.id}
                          >
                            {actingId === u.id
                              ? '...'
                              : u.isBlocked ? 'Blokdan chiqarish' : 'Bloklash'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={ui.pager}>
            <button
              style={{ ...ui.pagerBtn, ...(page <= 1 ? ui.btnDisabled : {}) }}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              ← Oldingi
            </button>
            <span style={ui.pagerInfo}>{page} / {totalPages} · jami {total} ta</span>
            <button
              style={{ ...ui.pagerBtn, ...(page >= totalPages ? ui.btnDisabled : {}) }}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Keyingi →
            </button>
          </div>
        </>
      )}

      {profile && (
        <Modal title="Foydalanuvchi profili" onClose={closeProfile} maxWidth="460px">
          {profileLoading || profile.__loading ? (
            <Loading />
          ) : profile.__error ? (
            <>
              <ErrorBanner>Profilni yuklab bo'lmadi.</ErrorBanner>
              <div style={ui.modalActions}>
                <button style={ui.cancelBtn} onClick={closeProfile}>Yopish</button>
                <button style={ui.saveBtn} onClick={() => openProfile(profile.id)}>Qayta urinish</button>
              </div>
            </>
          ) : (
            <>
              <div style={pf.head}>
                <Avatar src={profile.imageUrl} firstName={profile.firstName} lastName={profile.lastName} size={56} />
                <div>
                  <div style={pf.name}>
                    {[profile.firstName, profile.lastName].filter(Boolean).join(' ') || '—'}
                  </div>
                  <div style={pf.sub}>
                    <Badge tone={ROLE_TONE[profile.role] || 'gray'}>
                      {ROLE_LABELS[profile.role] || profile.role || '—'}
                    </Badge>
                    {profile.isBlocked
                      ? <Badge tone="red">Bloklangan</Badge>
                      : <Badge tone="green">Faol</Badge>}
                  </div>
                </div>
              </div>
              <Row label="Telefon" value={formatPhone(profile.phone)} />
              <Row label="Email" value={profile.login} />
              <Row label="Manzil" value={profile.address} />
              {isProviderRole(profile.role) && (
                <>
                  <Row label="Hudud" value={providerRegion(profile)} />
                  <div style={pf.row}>
                    <span style={pf.rowLabel}>Kategoriyalar</span>
                    <span style={pf.rowValue}>
                      {providerCategories(profile).length === 0 ? '—' : (
                        <span style={pf.chips}>
                          {providerCategories(profile).map((c) => (
                            <Badge key={c.id} tone="dark">{c.name}</Badge>
                          ))}
                        </span>
                      )}
                    </span>
                  </div>
                </>
              )}
              <Row label="Holat (status)" value={userStatusLabel(profile.status)} />
              <Row label="Moderatsiya" value={moderationStatusLabel(profile.moderationStatus)} />
              <Row label="MyID tasdiqlangan" value={profile.myidVerified ? 'Ha' : "Yo'q"} />
              <Row label="Ro'yxatdan o'tgan" value={fmtDate(profile.createdAt, true)} />

              {profile.role !== 'admin' && (
                <div style={ui.modalActions}>
                  <button
                    style={{
                      ...(profile.isBlocked ? ui.saveBtn : { ...ui.saveBtn, ...ui.btnRed }),
                      ...(actingId === profile.id ? ui.btnDisabled : {}),
                    }}
                    onClick={() => setConfirmUser(profile)}
                    disabled={actingId === profile.id}
                  >
                    {actingId === profile.id
                      ? 'Bajarilmoqda...'
                      : profile.isBlocked ? 'Blokdan chiqarish' : 'Bloklash'}
                  </button>
                </div>
              )}
            </>
          )}
        </Modal>
      )}

      {confirmUser && (
        <ConfirmDialog
          title={confirmUser.isBlocked ? 'Blokdan chiqarish' : 'Bloklash'}
          message={confirmUser.isBlocked ? 'Foydalanuvchini blokdan chiqarasizmi?' : 'Foydalanuvchini bloklaysizmi?'}
          confirmLabel="Ha"
          cancelLabel="Bekor qilish"
          danger={!confirmUser.isBlocked}
          onCancel={() => setConfirmUser(null)}
          onConfirm={() => toggleBlock(confirmUser)}
        />
      )}
    </AdminLayout>
  );
}

function Row({ label, value }) {
  return (
    <div style={pf.row}>
      <span style={pf.rowLabel}>{label}</span>
      <span style={pf.rowValue}>{value === null || value === undefined || value === '' ? '—' : value}</span>
    </div>
  );
}

const pf = {
  head: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' },
  chips: { display: 'inline-flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' },
  name: { fontSize: '17px', fontWeight: '700', color: 'var(--color-text)', marginBottom: '6px' },
  sub: { display: 'flex', gap: '6px' },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '9px 0',
    borderBottom: '1px solid var(--color-border)',
    fontSize: '14px',
  },
  rowLabel: { color: 'var(--color-text-muted)' },
  rowValue: { color: 'var(--color-text)', fontWeight: '500', textAlign: 'right' },
};
